import React from 'react';

import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Accordion, AccordionDetails, AccordionSummary, Alert, Chip, Stack, Typography } from '@mui/material';

import { pluralizeToString } from '~/utils/stringUtils';

import BlackoutDiffTable from '../BlackoutDiffTable';
import type { DeletedBlackoutRange } from '../blackoutReconciliation';
import { getDeletedRangeKey } from '../buildBlackoutWizardApplyPlan';
import type { RoomRateWizardPlan } from '../buildBlackoutWizardApplyPlan';

interface Props {
  excludedBlockKeys: ReadonlySet<string>;
  onToggleBlockExclusion: (range: DeletedBlackoutRange) => void;
  plans: Array<RoomRateWizardPlan>;
}

const AUTO_EXPAND_ACCORDION_THRESHOLD = 3;

export function BlackoutWizardSummary({ excludedBlockKeys, onToggleBlockExclusion, plans }: Props) {
  const changedPlans = plans.filter((plan) => plan.blocksToDelete.length > 0 || plan.rangesToCreate.length > 0);

  if (changedPlans.length === 0) {
    return (
      <Alert severity="info">Selected room rates already match these blackout dates. There is nothing to apply.</Alert>
    );
  }

  return (
    <Stack spacing={1}>
      {changedPlans.map((plan) => {
        const includedDeleteCount = plan.deletedRanges.filter(
          (range) => !excludedBlockKeys.has(getDeletedRangeKey(range)),
        ).length;

        return (
          <Accordion key={plan.roomRateId} defaultExpanded={changedPlans.length <= AUTO_EXPAND_ACCORDION_THRESHOLD}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Stack direction="row" alignItems="center" gap={1} sx={{ width: '100%' }}>
                <Typography variant="body2">
                  {plan.roomTypeName} — {plan.ratePlanName}
                </Typography>
                <Chip
                  size="small"
                  label={`${pluralizeToString('range', plan.rangesToCreate.length)} to create, ${pluralizeToString('block', includedDeleteCount)} to remove`}
                  color="warning"
                  variant="outlined"
                  sx={{ ml: 'auto' }}
                />
              </Stack>
            </AccordionSummary>
            <AccordionDetails>
              <BlackoutDiffTable
                deletedRanges={plan.deletedRanges}
                excludedBlockKeys={excludedBlockKeys}
                onToggleBlockExclusion={onToggleBlockExclusion}
                rangesToCreate={plan.rangesToCreate}
              />
            </AccordionDetails>
          </Accordion>
        );
      })}
    </Stack>
  );
}
